/**
 * Grouped annual bar geometry (ADR-0035). Pure numbers, no DOM — the same
 * split as `annual-chart.ts`. A missing series value reserves the slot and
 * is never plotted at zero (R2b).
 */
import { annualPlotBox, annualYAt, type AnnualPeriod, type AnnualPlotBox } from './annual-chart';

export interface GroupedAnnualSeries {
  readonly id: string;
  readonly values: readonly (number | null)[];
}

export interface GroupedAnnualBar {
  readonly year: number;
  readonly seriesId: string;
  readonly value: number | null;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly cx: number;
  readonly cy: number | null;
}

/** Inner padding of each year slot, as a fraction of slot width. */
export const GROUPED_SLOT_PAD_RATIO = 0.12;

function isFiniteValue(value: number | null): value is number {
  return value !== null && Number.isFinite(value);
}

function asPeriods(
  years: readonly number[],
  series: readonly GroupedAnnualSeries[],
): readonly AnnualPeriod[] {
  const yearSlots = years.map((year) => ({ year, value: null }));
  const cells = series.flatMap((row) =>
    years.map((year, index) => ({ year, value: row.values[index] ?? null })),
  );
  return [...yearSlots, ...cells];
}

export function groupedAnnualBox(
  years: readonly number[],
  series: readonly GroupedAnnualSeries[],
): AnnualPlotBox {
  return annualPlotBox(asPeriods(years, series));
}

function barRect(
  value: number,
  x: number,
  width: number,
  box: AnnualPlotBox,
): Pick<GroupedAnnualBar, 'x' | 'y' | 'width' | 'height' | 'cx' | 'cy'> {
  const zero = annualYAt(0, box);
  const top = annualYAt(value, box);
  if (value >= 0) {
    return { x, y: top, width, height: Math.max(0, zero - top), cx: x + width / 2, cy: top };
  }
  return { x, y: zero, width, height: Math.max(0, top - zero), cx: x + width / 2, cy: top };
}

function layoutYear(input: {
  readonly year: number;
  readonly slotX: number;
  readonly slotW: number;
  readonly series: readonly GroupedAnnualSeries[];
  readonly index: number;
  readonly box: AnnualPlotBox;
}): readonly GroupedAnnualBar[] {
  const { year, slotX, slotW, series, index, box } = input;
  const pad = slotW * GROUPED_SLOT_PAD_RATIO;
  const inner = Math.max(0, slotW - pad * 2);
  /* layoutGroupedAnnualBars returns [] when series.length === 0, so n > 0. */
  const n = series.length;
  const width = inner / n;
  const start = slotX + pad;
  return series.map((row, seriesIndex) => {
    const value = row.values[index] ?? null;
    const x = start + seriesIndex * width;
    if (!isFiniteValue(value)) {
      return {
        year,
        seriesId: row.id,
        value: null,
        x,
        y: annualYAt(0, box),
        width,
        height: 0,
        cx: x + width / 2,
        cy: null,
      };
    }
    return { year, seriesId: row.id, value, ...barRect(value, x, width, box) };
  });
}

export function layoutGroupedAnnualBars(
  years: readonly number[],
  series: readonly GroupedAnnualSeries[],
  box: AnnualPlotBox,
): readonly GroupedAnnualBar[] {
  const n = years.length;
  if (n === 0 || series.length === 0) return [];
  const slotW = box.w / n;
  return years.flatMap((year, index) =>
    layoutYear({ year, slotX: box.x + index * slotW, slotW, series, index, box }),
  );
}

export function groupedYearTickX(bars: readonly GroupedAnnualBar[], year: number): number | null {
  const inYear = bars.filter((bar) => bar.year === year);
  const first = inYear[0];
  const last = inYear[inYear.length - 1];
  if (first === undefined || last === undefined) return null;
  return (first.x + last.x + last.width) / 2;
}

export function groupedSeriesLinePath(
  bars: readonly GroupedAnnualBar[],
  seriesId: string,
): string | null {
  const parts = bars.reduce<{ readonly cmds: readonly string[]; readonly penDown: boolean }>(
    (acc, bar) => {
      if (bar.seriesId !== seriesId || bar.cy === null) {
        return bar.seriesId === seriesId ? { cmds: acc.cmds, penDown: false } : acc;
      }
      const cmd = `${acc.penDown ? 'L' : 'M'} ${String(bar.cx)} ${String(bar.cy)}`;
      return { cmds: [...acc.cmds, cmd], penDown: true };
    },
    { cmds: [], penDown: false },
  );
  return parts.cmds.length === 0 ? null : parts.cmds.join(' ');
}
